/**
 * Mable Events Task Processor
 * Handles tasks specific to the Mable Events worker
 */

import { Logger } from '@/shared/logger/logger';
import { SourcesMableEvent } from '@mable-ai/seo-client';

import {
  BaseTaskProcessor,
  callAPI,
  TaskHandlerRegistry,
} from '../shared/task_handler';
import { TaskResult } from '../shared/worker_utils';

/**
 * Send Mable Event task handler
 * @param payload - The payload containing the Mable event and API URL
 */
export async function sendMableEvent(payload: {
  apiUrl: string;
  mableEvent: SourcesMableEvent;
}): Promise<TaskResult> {
  try {
    // Validate the event
    if (!payload.mableEvent || !payload.apiUrl) {
      throw new Error('Invalid payload: missing mableEvent or apiUrl');
    }

    // Log event details (safely accessing properties)
    Logger.info('[Events Worker] Sending Mable event:', {
      apiUrl: payload.apiUrl,
      type: typeof payload.mableEvent,
    });

    // Send the event to the API
    return await callAPI(payload.apiUrl, payload.mableEvent, 3, 'Events');
  } catch (error: any) {
    Logger.error('[Events Worker] Error sending Mable event:', error.message);
    return {
      error: error.message,
      success: false,
    };
  }
}

/**
 * Mable Events task processor
 * Responsible for handling all tasks for the Mable Events worker
 */
export class MableEventsTaskProcessor extends BaseTaskProcessor {
  /**
   * Worker name for logging
   */
  protected workerName = 'Mable Events';

  /**
   * Constructor
   */
  constructor() {
    // Define task handlers specific to Mable Events worker
    const eventTaskHandlers: TaskHandlerRegistry = {
      SEND_MABLE_EVENT: sendMableEvent,
    };

    // Initialize with Mable Events specific handlers
    super(eventTaskHandlers);
  }
}
